import { Button } from "@fluentui/react-components";
import {
  DeleteRegular,
  EyeTrackingOffRegular,
  EyeTrackingRegular,
} from "@fluentui/react-icons";
import { SaveIcon } from "@fluentui/react-icons-mdl2";
import { ButtonWithTooltip } from "components/ButtonWithTooltip/ButtonWithTooltip";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import {
  EAttachmentType,
  IFormattedFile,
  IFormattedFileExtended,
  SelectItem,
} from "library/types";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AttachmentTypeSelect } from "./AttachmentTypeSelect";
import { useCallback, useState } from "react";

type Props = {
  attachment: IFormattedFileExtended;
  createAttachmentInProgress?: boolean;
  deleteFromList: (attachment: IFormattedFileExtended) => void;
  showIsAttachHiddenForEmployeeField: boolean;
  updateAttachList: (attachment: IFormattedFileExtended) => void;
  showAttachmentTypeSelection?: boolean;
  onCreateAttachment: (
    attach: IFormattedFile & {
      isHiddenForEmployee?: boolean;
    },
    isHiddenForEmployee?: boolean,
    newAttachList?: IFormattedFileExtended[],
    onSuccessFn?: () => void,
    attachmentType?: EAttachmentType
  ) => void;
};

export function AddAttachItem({
  attachment,
  deleteFromList,
  createAttachmentInProgress,
  showIsAttachHiddenForEmployeeField,
  showAttachmentTypeSelection,
  onCreateAttachment,
  updateAttachList,
}: Props) {
  const [attachmentType, setAttachmentType] = useState<SelectItem | null>(null);
  const theme = useAppTheme();
  const { t } = useTranslation();

  const handleAttachmentTypeChange = useCallback((attachmentType) => {
    setAttachmentType(attachmentType);
  }, []);

  const isAttachHiddenForEmployee = attachment?.isAttachHiddenForEmployee;

  return (
    <Container>
      <div>{attachment?.name}</div>
      <Flex wrap="nowrap">
        {showAttachmentTypeSelection && (
          <AttachmentTypeSelect
            handleAttachmentTypeChange={handleAttachmentTypeChange}
            attachmentType={attachmentType}
          />
        )}
        {showIsAttachHiddenForEmployeeField && (
          <ButtonWithTooltip
            data-help="attachments-queued-visibility"
            disabled={createAttachmentInProgress}
            appearance="subtle"
            tooltipContent={
              isAttachHiddenForEmployee
                ? t("hbh.isHiddenForEmployee.label")
                : t("hbh.isVisibleForEmployee.label")
            }
            onClick={() => {
              const updatedAttachment = {
                ...attachment,
                isAttachHiddenForEmployee: !isAttachHiddenForEmployee,
              };
              updateAttachList(updatedAttachment);
            }}
            icon={
              isAttachHiddenForEmployee ? (
                <EyeTrackingOffRegular />
              ) : (
                <EyeTrackingRegular />
              )
            }
          />
        )}
        <Button
          data-help="attachments-queued-save"
          disabled={createAttachmentInProgress}
          style={{
            padding: "5px 15px",
          }}
          icon={
            <SaveIcon
              style={{
                color: theme.palette.purpleDark,
                opacity: createAttachmentInProgress ? "0.5" : "1",
              }}
            />
          }
          // disabled={updateInProgress}
          appearance="outline"
          size="small"
          onClick={async (e) => {
            e.stopPropagation();
            onCreateAttachment(
              attachment,
              isAttachHiddenForEmployee,
              undefined,
              () => {
                deleteFromList(attachment);
              },
              (attachmentType?.value as EAttachmentType) || undefined
            );
          }}
        />
        <Button
          disabled={createAttachmentInProgress}
          onClick={() => {
            deleteFromList(attachment);
          }}
          icon={<DeleteRegular />}
        />
      </Flex>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px;
  margin-bottom: 10px;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
`;
